import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from './AuthContext.jsx'
import { useConsent } from './ConsentContext.jsx'
import { fetchMySubscription } from '@/lib/billingApi.js'
import { deriveAdsSubscriptionStatus, resolveAdsCheckKey, resolveAdsUserKey } from '@/lib/adsVisibility.js'
import { advertisingPageFamily, resolveAdvertisingPolicy } from '@/lib/advertisingPolicy.js'
import { resolveAdProviderPriority, isAdsMockEnabled } from '@/config/adFlags.js'
import { authStore } from '@/lib/auth.js'
import { loadAdsConfig, normalizeAdsConfig } from '@/config/adExperiments.js'
import { adSessionBucket } from '@/lib/adExperiments.js'

const AdvertisingContext = createContext({ eligible: false, mockEnabled: false, reason: 'provider_missing', ageTreatment: null, pageFamily: 'editor' })

export function AdvertisingProvider({ children }) {
  const { user, entitlements } = useAuth() || {}
  const { consent } = useConsent()
  const { pathname } = useLocation()
  const product = pathname.startsWith('/jogos/nexo') ? 'nexo' : 'gusto'
  const [, refreshAccess] = useState(0)
  const accessExpiry = entitlements?.products?.nexo?.own_access_until
  useEffect(() => {
    if (product !== 'nexo' || !accessExpiry) return undefined
    const end = new Date(accessExpiry).getTime()
    if (!Number.isFinite(end) || end <= Date.now()) return undefined
    let timer
    const checkExpiry = () => {
      const remaining = end - Date.now()
      if (remaining <= 0) refreshAccess(value => value + 1)
      else timer = setTimeout(checkExpiry, Math.min(remaining + 50, 2147483647))
    }
    checkExpiry()
    return () => clearTimeout(timer)
  }, [product, accessExpiry])
  const mockEnabled = isAdsMockEnabled()
  const [adConfig, setAdConfig] = useState(() => normalizeAdsConfig())
  const [configReady, setConfigReady] = useState(() => !import.meta.env.VITE_ADS_RUNTIME_CONFIG_URL)
  useEffect(() => {
    if (!import.meta.env.VITE_ADS_RUNTIME_CONFIG_URL) return undefined
    let active = true
    loadAdsConfig().then(config => { if (active) { setAdConfig(config); setConfigReady(true) } })
    return () => { active = false }
  }, [])
  const checkKey = resolveAdsCheckKey(resolveAdsUserKey(user), entitlements, product)
  const inFlight = useRef(null)
  const [subscription, setSubscription] = useState({ checkKey: '', status: 'checking' })
  useEffect(() => {
    if (mockEnabled || !checkKey) { inFlight.current = null; return undefined }
    let active = true
    // Reuse across StrictMode replay, never across identities.
    if (inFlight.current?.checkKey !== checkKey) {
      const billing = product === 'nexo' ? fetchMySubscription({ product }) : fetchMySubscription()
      inFlight.current = { checkKey, promise: billing.then(data => deriveAdsSubscriptionStatus(data, product)).catch(() => 'error') }
    }
    inFlight.current.promise.then(status => { if (active) setSubscription({ checkKey, status }) })
    return () => { active = false }
  }, [checkKey, mockEnabled, product])
  const value = useMemo(() => {
    const subscriptionStatus = deriveAdsSubscriptionStatus(entitlements, product) === 'paid' ? 'paid' : subscription.checkKey === checkKey ? subscription.status : 'checking'
    // A failed /accounts/me/ can leave credentials present while AuthProvider
    // exposes false. That is unresolved authentication, not a confirmed guest.
    const unresolvedGuest = user === false && Boolean(authStore.getAccess() || authStore.getRefresh())
    const policy = resolveAdvertisingPolicy({ user: unresolvedGuest ? null : user, consent, pathname, subscriptionStatus })
    // Adsterra has no age-treatment signal in this integration. Known minors
    // receive only Google; anonymous and unknown-age visitors retain priority.
    const providers = policy.eligible ? resolveAdProviderPriority().filter(provider => provider === 'google' || (policy.advertisingConsent === 'granted' && policy.ageTreatment === 'UNSPECIFIED')) : []
    return {
      ...policy,
      eligible: configReady && !mockEnabled && providers.length > 0,
      providers,
      reason: !configReady ? 'configuration_pending' : policy.reason,
      // Local boxes can preview premium/consent states, but sensitive pages
      // retain their existing exclusion from advertising placements.
      mockEnabled: mockEnabled && policy.reason !== 'sensitive_route',
      pageFamily: advertisingPageFamily(pathname),
      pageKey: pathname,
      adConfig,
      experimentBucket: !mockEnabled && policy.eligible && Object.values(adConfig.experiments).some(experiment => experiment.enabled) ? adSessionBucket({ persist: policy.advertisingConsent === 'granted' }) : null,
    }
  }, [user, consent, pathname, product, subscription, checkKey, entitlements, mockEnabled, adConfig, configReady])
  return <AdvertisingContext.Provider value={value}>{children}</AdvertisingContext.Provider>
}

export const useAdvertising = () => useContext(AdvertisingContext)
